import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck, Route, Globe2, Bus, Zap, UtensilsCrossed, ArrowRight,
  UserCheck, Users, Briefcase, Users2, Heart, Sparkles, GraduationCap,
  MoonStar, Plane, Building2, Star, Megaphone,
} from "lucide-react";
import SEO from "../lib/seo";
import KineticHero from "../components/KineticHero";
import Marquee from "../components/Marquee";
import TripFinder from "../components/TripFinder";
import DestinationCard from "../components/DestinationCard";
import PackageCard from "../components/PackageCard";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { getDestinations, getPackages, getFaqs, getTestimonials } from "../lib/api";
import { useLead } from "../lib/store";
import { track } from "../lib/track";

const WHY = [
  { icon: BadgeCheck, title: "Professional Service", desc: "Tim berpengalaman menangani perjalanan internasional & domestik, dari perencanaan hingga pendampingan keberangkatan.", big: true },
  { icon: Route, title: "Custom Itinerary", desc: "Rute dan program dirancang sesuai kebutuhan, tempo, dan budget Anda." },
  { icon: Globe2, title: "International Destinations", desc: "11+ destinasi internasional dengan jaringan partner lokal terpercaya." },
  { icon: Bus, title: "Comfortable Transportation", desc: "Private van & bus full AC dengan driver berpengalaman di negara tujuan." },
  { icon: Zap, title: "Fast Response", desc: "Respon cepat untuk konsultasi, quotation, dan selama perjalanan berlangsung." },
  { icon: UtensilsCrossed, title: "Halal Travel Support", desc: "Rekomendasi restoran halal dan fasilitas ramah Muslim di setiap destinasi — dari qingzhen di China hingga default halal di Turki dan Dubai. Kami bantu susun makan sesuai kebutuhan group Anda.", wide: true },
];

const STEPS = [
  { title: "Tell Us Your Plan", desc: "Ceritakan destinasi, tanggal, jumlah peserta, dan gaya perjalanan Anda." },
  { title: "Get Your Proposal", desc: "Kami susun proposal & quotation yang sesuai kebutuhan perjalanan Anda." },
  { title: "Confirm Your Booking", desc: "Setujui program, lakukan booking, dan kami kunci seluruh layanan." },
  { title: "Enjoy Your Journey", desc: "Nikmati perjalanan dengan local support kami di negara tujuan." },
];

const SERVICES = [
  { icon: Route, label: "Land Arrangement" },
  { icon: UserCheck, label: "Private Trip" },
  { icon: Users, label: "Open Trip" },
  { icon: Briefcase, label: "Corporate Travel" },
  { icon: Users2, label: "Group Tour" },
  { icon: Heart, label: "Family Trip" },
  { icon: Sparkles, label: "Honeymoon" },
  { icon: GraduationCap, label: "Study Tour" },
  { icon: MoonStar, label: "Halal Tour" },
  { icon: Plane, label: "Airport Transfer" },
  { icon: Bus, label: "Bus Rental" },
  { icon: Building2, label: "Hotel Booking" },
];

const GROUP_TARGETS = ["Corporate", "School", "University", "Community", "Organization", "Family Group"];
const GROUP_SERVICES = ["Group Land Arrangement", "Study Tour", "Company Gathering", "Incentive Trip", "Business Trip", "Industry Visit"];

const TESTIMONIALS = [
  { trip: "Trip China" },
  { trip: "Trip Turkey" },
  { trip: "Trip Japan" },
];

const CORP_IMG = "https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1600";

export default function Home() {
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { openLead } = useLead();

  useEffect(() => {
    getDestinations().then(setDestinations).catch(() => {});
    getPackages({ featured: "true" }).then(setPackages).catch(() => {});
    getFaqs().then((f) => setFaqs(f.slice(0, 6))).catch(() => {});
    getTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Beyond Travel — Land Arrangement & Paket Perjalanan Internasional"
        description="Land Arrangement internasional: itinerary, hotel, transportasi lokal, guide, tiket atraksi, makan, dan airport transfer. Travel More, Explore More."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: "Beyond Travel",
          slogan: "Travel More, Explore More",
          url: "https://beyondtravel.co.id",
          telephone: "+628812551877",
          email: "info@beyondtravel.co.id",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Ruko U-Ville Avenue, Jl. Boulevard UPJ Ruko U-Ville A No.5, Sawah Baru, Ciputat",
            addressLocality: "Tangerang Selatan",
            addressRegion: "Banten",
            postalCode: "15413",
            addressCountry: "ID",
          },
        }}
      />
      <KineticHero />
      <Marquee />
      <section className="bg-cream pb-4 pt-0">
        <TripFinder />
      </section>

      <section className="section bg-cream pt-8" data-testid="home-destinations">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading
              eyebrow="Destinations"
              title="Explore Our Destinations"
              sub="Pilihan destinasi internasional dengan layanan perjalanan yang dapat disesuaikan dengan kebutuhan Anda."
            />
            <Reveal delay={0.2}>
              <Link to="/destinations" className="btn btn-outline" data-testid="home-destinations-view-all">
                Semua Destinasi <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
            {destinations.slice(0, 8).map((d, i) => (
              <DestinationCard key={d.slug} destination={d} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white" data-testid="home-packages">
        <div className="container-x">
          <SectionHeading
            eyebrow="Land Arrangement"
            title="Land Arrangement Packages"
            sub="Beli layanan perjalanan di negara tujuan tanpa terikat satu paket perjalanan yang kaku. Semua dapat disesuaikan dengan kebutuhan Anda."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {packages.slice(0, 3).map((p, i) => (
              <PackageCard key={p.slug} pkg={p} index={i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 text-center">
              <Link to="/land-arrangement" className="btn btn-primary" data-testid="home-packages-view-all">
                Lihat Paket Land Arrangement <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section bg-cream" data-testid="home-why">
        <div className="container-x">
          <SectionHeading
            eyebrow="Why Beyond Travel"
            title="Why Travel With Beyond Travel?"
            sub="Satu partner untuk seluruh kebutuhan perjalanan Anda di negara tujuan."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal
                key={w.title}
                delay={Math.min(i * 0.07, 0.3)}
                className={`h-full ${w.big ? "md:col-span-2" : ""} ${w.wide ? "md:col-span-3" : ""}`}
              >
                <div
                  className={`flex h-full flex-col justify-between rounded-3xl p-6 md:p-8 ${
                    w.big || w.wide
                      ? "bg-tosca-600 text-white shadow-[0_16px_40px_-12px_rgba(15,118,110,0.45)]"
                      : "bg-white text-charcoal shadow-crystal card-hover"
                  }`}
                  data-testid={`why-card-${i + 1}`}
                >
                  <div className="flex items-start justify-between">
                    <w.icon className={`h-7 w-7 ${w.big || w.wide ? "text-gold" : "text-tosca-600"}`} />
                    <span className={`font-heading text-3xl font-black ${w.big || w.wide ? "text-white/20" : "text-tosca-100"}`}>
                      0{i + 1}
                    </span>
                  </div>
                  <div className="mt-8">
                    <h3 className="font-heading text-lg font-bold sm:text-xl">{w.title}</h3>
                    <p className={`mt-2 text-sm leading-relaxed ${w.big || w.wide ? "text-white/75" : "text-slate-500"}`}>{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white" data-testid="home-how-it-works">
        <div className="container-x">
          <SectionHeading
            eyebrow="How It Works"
            title="4 Langkah Menuju Perjalanan Anda"
            sub="Proses yang jelas dan didampingi tim kami di setiap tahap."
          />
          <div className="relative mt-12 grid gap-8 md:grid-cols-4">
            <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-gold/60 via-tosca-300 to-gold/60 md:block" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="relative" data-testid={`how-step-${i + 1}`}>
                  <div className="relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-full bg-tosca-600 font-heading text-lg font-black text-white ring-4 ring-gold/40">
                    0{i + 1}
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold text-charcoal sm:text-lg">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-tosca-900" data-testid="home-services">
        <div className="container-x">
          <SectionHeading
            eyebrow="Our Services"
            title="One Partner, Complete Travel Solution"
            sub="Dari land arrangement hingga bus rental — semua kebutuhan perjalanan dalam satu partner."
            dark
          />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.label} delay={Math.min(i * 0.05, 0.3)}>
                <Link
                  to="/services"
                  className="group flex h-full flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:bg-white/10"
                  data-testid={`home-service-${s.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <s.icon className="h-6 w-6 text-gold" />
                  <span className="text-sm font-semibold text-white">{s.label}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="home-corporate">
        <div className="absolute inset-0">
          <img src={CORP_IMG} alt="" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-tosca-900/95 via-tosca-900/80 to-tosca-900/40" />
        </div>
        <div className="container-x relative py-20 md:py-28">
          <div className="max-w-xl">
            <Reveal><p className="eyebrow !text-gold">Corporate & Group</p></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                Corporate & Group Travel
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-4 text-sm leading-relaxed text-white/75 sm:text-base">
                Perjalanan group untuk perusahaan, sekolah, universitas, komunitas, dan organisasi — dengan penawaran khusus dan pendampingan penuh.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-6 flex flex-wrap gap-2">
                {GROUP_TARGETS.map((t) => (
                  <span key={t} className="chip glass-dark text-white/90">{t}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2">
                {GROUP_SERVICES.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-white/85">
                    <Megaphone className="h-4 w-4 flex-shrink-0 text-gold" /> {s}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.34}>
              <button
                onClick={() => {
                  track("submit_group_inquiry", { source: "home_corporate_band", intent: true });
                  openLead({ type: "group" });
                }}
                className="btn btn-gold mt-8 !px-8 !py-3.5"
                data-testid="home-corporate-proposal-cta"
              >
                Request Group Proposal
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section bg-cream" data-testid="home-testimonials">
        <div className="container-x">
          <SectionHeading
            eyebrow="Testimonials"
            title="What Our Travelers Say"
            sub={testimonials.length > 0
              ? "Ulasan asli dari pelanggan Beyond Travel."
              : "Testimonial pelanggan sedang dalam proses pengumpulan. Berikut tempat yang akan diisi oleh review asli mereka."}
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(testimonials.length > 0 ? testimonials : TESTIMONIALS).map((t, i) => (
              <Reveal key={t.id || t.trip} delay={i * 0.1}>
                <div className="flex h-full flex-col rounded-3xl bg-white p-6 shadow-crystal card-hover" data-testid={`testimonial-card-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-tosca-600 font-heading text-sm font-bold text-white">
                        {(t.name || "NC").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-heading text-sm font-bold text-charcoal">{t.name || "Nama Customer"}</p>
                      <p className="text-xs text-slate-400">{t.trip}</p>
                    </div>
                    {testimonials.length === 0 && (
                      <span className="ml-auto chip bg-gold-light text-gold-dark">Placeholder</span>
                    )}
                  </div>
                  <div className="mt-4 flex gap-0.5">
                    {[...Array(t.rating || 5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-gold text-gold" />
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">
                    {t.review || "Tempat review pelanggan. Testimonial asli akan kami tampilkan setelah tersedia."}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white" data-testid="home-faq">
        <div className="container-x grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="FAQ"
              title="Pertanyaan yang Sering Diajukan"
              sub="Belum menemukan jawaban? Tim kami siap membantu lewat WhatsApp."
            />
            <Reveal delay={0.2}>
              <Link to="/faq" className="btn btn-outline mt-6" data-testid="home-faq-view-all">
                Lihat Semua FAQ <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <Reveal>
              <FaqAccordion items={faqs} />
            </Reveal>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
